import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { Loader2, Wand2 } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { PrimaryButton, SecondaryButton, Field, Select } from "./AdminUI";

export function ImportClothingFromUrl({ open, onOpenChange }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [brandId, setBrandId] = useState("");
  const [usePaid, setUsePaid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needsBrand, setNeedsBrand] = useState(false);

  const { data: brands = [] } = useQuery({
    queryKey: ["admin-brands"],
    queryFn: async () => (await api.get("/admin/brands")).data,
    enabled: open,
  });

  function reset() {
    setUrl("");
    setBrandId("");
    setUsePaid(false);
    setNeedsBrand(false);
    setSubmitting(false);
  }

  async function submit() {
    if (!url.trim()) {
      toast.error("Paste a product URL first");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        product_url: url.trim(),
        use_scraperapi: usePaid,
        ...(brandId ? { brand_id: brandId } : {}),
      };
      const { data } = await api.post("/admin/clothing-items/import-from-url", body);
      toast.success(`Imported '${data.name}' — set to pending review.`);
      qc.invalidateQueries({ queryKey: ["admin-clothing"] });
      reset();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "brand_not_resolved") {
        setNeedsBrand(true);
        toast.error(detail.message || "Pick the brand for this URL");
      } else if (code === "duplicate_clothing_item") {
        toast.error(detail.message);
        if (detail.clothing_item_id) {
          // jump to the existing item
          onOpenChange(false);
          navigate(`/admin/clothing-items/${detail.clothing_item_id}`);
        }
      } else if (code === "duplicate_crawler_result") {
        toast.error(detail.message);
        if (detail.brand_id) {
          onOpenChange(false);
          navigate(`/admin/crawler-results?brand_id=${detail.brand_id}`);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Import failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-lg" data-testid="import-from-url-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4" /> Import from URL</DialogTitle>
          <DialogDescription>
            Paste a product page URL. We'll fetch it, extract the name, image, price and materials
            with the LLM, then add it as a <strong>pending review</strong> item.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Product URL">
            <input
              type="url"
              autoFocus
              value={url}
              onChange={(e) => { setUrl(e.target.value); setNeedsBrand(false); }}
              placeholder="https://brand.com/products/some-item"
              className="w-full h-10 px-3 border border-[#E9ECEF] rounded-md text-sm"
              data-testid="import-url-input"
            />
          </Field>

          <Field
            label={needsBrand ? "Brand (required — no brand matched the URL host)" : "Brand (optional — auto-detected from URL host)"}
          >
            <Select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="import-brand-select">
              <option value="">Auto-detect from URL host</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </Select>
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={usePaid}
              onChange={(e) => setUsePaid(e.target.checked)}
              data-testid="import-use-paid-toggle"
            />
            <span>Use paid proxy (ScraperAPI) — needed for sites with strict bot protection</span>
          </label>
        </div>

        <DialogFooter>
          <SecondaryButton onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={submitting} data-testid="import-submit-button">
            {submitting ? (<><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Importing…</>) : "Import"}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
